import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuActivity, LuCheck, LuChevronRight, LuCircleAlert, LuClock3, LuRefreshCw, LuX } from 'react-icons/lu';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useScope } from '../contexts/ScopeContext';
import { api } from '../utils/api';
import { recoveryGuidance, statusLabel, statusTone } from '../utils/workHelpers';


const toneClasses = {
  emerald: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
  red: 'border-red-500/20 bg-red-500/10 text-red-300',
  amber: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
  zinc: 'border-zinc-700 bg-zinc-800/70 text-zinc-300',
};

const messageFor = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
};

const StatusPill = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${toneClasses[statusTone(status)]}`}>
    {statusLabel(status)}
  </span>
);

const EmptyState = ({ title, detail, action = null }) => (
  <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-900/30 px-6 py-12 text-center">
    <LuCheck className="mx-auto h-7 w-7 text-zinc-600" />
    <h2 className="mt-3 font-medium text-zinc-200">{title}</h2>
    <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">{detail}</p>
    {action}
  </div>
);

const WorkCard = ({ item, onOpen, task = false }) => {
  const operationStatus = item.operation?.status;
  const guidance = recoveryGuidance(operationStatus);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 text-left transition hover:border-zinc-700 hover:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-zinc-100">
              {item.action ? `${statusLabel(item.action)} ${item.target?.display_name}` : item.target?.display_name}
            </p>
            <StatusPill status={operationStatus || item.status} />
          </div>
          <p className="mt-1 truncate text-sm text-zinc-500">
            {task ? `Requested by ${item.requester?.display_name}` : item.reason || 'No reason supplied'}
          </p>
          {item.approval?.step_label && item.status === 'awaiting_approval' && (
            <p className="mt-2 text-xs text-amber-300">
              {item.approval.step_label} · {item.approval.approvals_received}/{item.approval.approvals_required} approvals
            </p>
          )}
          {guidance && <p className="mt-2 text-xs leading-5 text-zinc-400">{guidance}</p>}
        </div>
        <LuChevronRight className="mt-1 h-5 w-5 shrink-0 text-zinc-600" />
      </div>
    </button>
  );
};

const OperationCard = ({ operation, onOpen }) => {
  const guidance = recoveryGuidance(operation.status);
  const targetLabel = operation.target_display_name || operation.target_id || 'Unknown server';
  return (
    <button
      type="button"
      onClick={() => operation.request_id && onOpen({ id: operation.request_id, target: { display_name: targetLabel } })}
      disabled={!operation.request_id}
      className="w-full rounded-xl border border-zinc-800 bg-zinc-900/70 p-4 text-left transition hover:border-zinc-700 hover:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 disabled:cursor-default disabled:hover:border-zinc-800"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium text-zinc-100">{statusLabel(operation.kind?.replace('resource.', '') || 'Provider operation')} · {targetLabel}</p>
          <p className="mt-1 text-xs text-zinc-500">Reference {operation.correlation_id || operation.id}</p>
        </div>
        <StatusPill status={operation.status} />
      </div>
      {guidance && <p className="mt-3 text-sm leading-6 text-zinc-400">{guidance}</p>}
    </button>
  );
};

const Timeline = ({ timeline, loading }) => {
  if (loading) return <p className="py-8 text-center text-sm text-zinc-500" role="status">Loading history…</p>;
  if (!timeline?.events?.length) {
    return <p className="py-8 text-center text-sm text-zinc-500">Detailed history is not available for this earlier request.</p>;
  }
  return (
    <>
      {timeline.history_complete === false && (
        <div className="mb-5 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm leading-6 text-amber-200" role="status">
          This is a partial history. The current request status is authoritative, but some earlier detailed evidence is unavailable.
        </div>
      )}
      <ol className="space-y-0" aria-label="Request history">
      {timeline.events.map((event, index) => (
        <li key={event.id} className="relative flex gap-3 pb-6 last:pb-0">
          {index < timeline.events.length - 1 && <span className="absolute left-[7px] top-5 h-full w-px bg-zinc-800" />}
          <span className={`relative mt-1.5 h-4 w-4 shrink-0 rounded-full border-4 border-zinc-950 ${event.status === 'succeeded' ? 'bg-emerald-400' : event.status === 'failed' ? 'bg-red-400' : 'bg-amber-400'}`} />
          <div>
            <p className="text-sm font-medium text-zinc-200">{event.title}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {event.actor?.display_name} · {event.occurred_at ? new Date(event.occurred_at).toLocaleString() : 'Time unavailable'}
            </p>
            {recoveryGuidance(event.status) && <p className="mt-2 text-xs leading-5 text-amber-200">{recoveryGuidance(event.status)}</p>}
          </div>
        </li>
      ))}
      </ol>
    </>
  );
};

const MyWork = ({ user, onLogout }) => {
  const scope = useScope();
  const [searchParams] = useSearchParams();
  const organizationId = scope.organization?.id;
  const projectId = scope.project?.id;
  const [activeTab, setActiveTab] = useState('requests');
  const [data, setData] = useState({ my_requests: [], approval_tasks: [], active_operations: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [decision, setDecision] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const timelineSequence = useRef(0);
  const openedDeepLink = useRef(null);

  const hasActive = useMemo(
    () => (data.active_operations || []).length > 0 || (data.my_requests || []).some((item) => !['succeeded', 'failed', 'rejected', 'cancelled', 'expired'].includes(item.status)),
    [data]
  );

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!organizationId || !projectId) return;
    quiet ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const response = await api.getMyWork(organizationId, projectId);
      setData(response.data);
      setSelected((current) => {
        if (!current) return null;
        return [...(response.data.my_requests || []), ...(response.data.approval_tasks || [])]
          .find((item) => item.id === current.id) || null;
      });
    } catch (requestError) {
      if (requestError.code !== 'ERR_CANCELED') setError(messageFor(requestError, 'Your work could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [organizationId, projectId]);

  useEffect(() => {
    setData({ my_requests: [], approval_tasks: [], active_operations: [], counts: {} });
    setSelected(null);
    setTimeline(null);
    load();
  }, [scope.version, load]);

  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = window.setInterval(() => load({ quiet: true }), 8000);
    return () => window.clearInterval(timer);
  }, [hasActive, load]);

  const loadTimeline = async (requestId) => {
    const sequence = ++timelineSequence.current;
    setTimeline(null);
    setTimelineLoading(true);
    try {
      const response = await api.getRequisitionTimeline(organizationId, projectId, requestId);
      if (sequence === timelineSequence.current) {
        setTimeline(response.data.timeline);
        if (response.data.timeline?.request) setSelected(response.data.timeline.request);
      }
    } catch (requestError) {
      if (requestError.code !== 'ERR_CANCELED') toast.error(messageFor(requestError, 'Request history could not be loaded'));
    } finally {
      if (sequence === timelineSequence.current) setTimelineLoading(false);
    }
  };

  const openRequest = async (item) => {
    setSelected(item);
    await loadTimeline(item.id);
  };

  useEffect(() => {
    const requestId = searchParams.get('request');
    const key = requestId ? `${scope.version}:${requestId}` : null;
    if (!requestId || openedDeepLink.current === key) return;
    openedDeepLink.current = key;
    openRequest({ id: requestId, target: { display_name: 'Request' }, viewer_actions: {} });
  }, [scope.version, searchParams]);

  const submitDecision = async () => {
    const precondition = selected?.viewer_actions?.decision_precondition;
    if (!precondition || !decision) return;
    if (decision === 'reject' && comment.trim().length < 3) {
      toast.error('Please explain why this request is being rejected.');
      return;
    }
    setBusy(true);
    try {
      await api.decideRequisition(organizationId, projectId, selected.id, decision, {
        expected_content_hash: precondition.content_hash,
        expected_approval_revision: precondition.approval_revision,
        step_id: precondition.step_id,
        comment: comment.trim() || null,
      });
      toast.success(decision === 'approve' ? 'Approval recorded' : 'Request rejected');
      setDecision(null);
      setComment('');
      setSelected(null);
      await load({ quiet: true });
    } catch (requestError) {
      if (requestError.response?.status === 409) {
        toast.error('This request changed while you were reviewing it. The latest state has been loaded; your comment is still here.');
        await load({ quiet: true });
        await loadTimeline(selected.id);
      } else if (requestError.response?.status === 403) {
        toast.error('Your approval access changed. The latest permissions have been loaded.');
        setDecision(null);
        await load({ quiet: true });
      } else {
        toast.error(messageFor(requestError, 'The decision could not be recorded'));
      }
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    const revision = selected?.viewer_actions?.cancel_precondition?.revision;
    if (!revision || !window.confirm('Cancel this request before it reaches the provider?')) return;
    setBusy(true);
    try {
      await api.cancelRequisition(organizationId, projectId, selected.id, revision);
      toast.success('Request cancelled');
      await load({ quiet: true });
      await loadTimeline(selected.id);
    } catch (requestError) {
      toast.error(messageFor(requestError, 'The request could not be cancelled'));
      if (requestError.response?.status === 409) await load({ quiet: true });
    } finally {
      setBusy(false);
    }
  };

  const loadMore = async () => {
    const isRequests = activeTab === 'requests';
    const cursor = isRequests ? data.page?.next_request_cursor : data.page?.next_operation_cursor;
    if (!cursor || activeTab === 'approvals') return;
    setLoadingMore(true);
    try {
      const response = await api.getMyWork(organizationId, projectId, isRequests
        ? { request_cursor: cursor }
        : { operation_cursor: cursor });
      setData((current) => ({
        ...current,
        my_requests: isRequests
          ? [...current.my_requests, ...(response.data.my_requests || [])]
          : current.my_requests,
        active_operations: isRequests
          ? current.active_operations
          : [...current.active_operations, ...(response.data.active_operations || [])],
        page: {
          ...current.page,
          ...(isRequests
            ? { next_request_cursor: response.data.page?.next_request_cursor }
            : { next_operation_cursor: response.data.page?.next_operation_cursor }),
        },
      }));
    } catch (requestError) {
      toast.error(messageFor(requestError, 'More work could not be loaded'));
    } finally {
      setLoadingMore(false);
    }
  };

  const tabs = [
    { id: 'requests', label: 'My requests', count: data.my_requests?.length || 0 },
    { id: 'approvals', label: 'Approvals', count: data.counts?.approval_tasks || 0 },
    { id: 'operations', label: 'Active operations', count: data.active_operations?.length || 0 },
  ];

  const content = activeTab === 'requests' ? data.my_requests : activeTab === 'approvals' ? data.approval_tasks : data.active_operations;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar user={user} onLogout={onLogout} />
      <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-amber-400">Governed operations</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">My Work</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-400">Request changes, review approvals, and follow provider work through confirmed completion.</p>
          </div>
          <button onClick={() => load({ quiet: true })} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-700 disabled:opacity-50">
            <LuRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto border-b border-zinc-800" role="tablist" aria-label="Work views">
          {tabs.map((tab) => (
            <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${activeTab === tab.id ? 'border-amber-400 text-amber-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
              {tab.label} <span className="ml-1 rounded-full bg-zinc-800 px-2 py-0.5 text-xs">{tab.count}</span>
            </button>
          ))}
        </div>

        {error ? (
          <div className="mt-6 rounded-xl border border-red-500/20 bg-red-500/10 p-5" role="alert">
            <div className="flex gap-3"><LuCircleAlert className="mt-0.5 h-5 w-5 text-red-300" /><div><p className="font-medium text-red-200">My Work is unavailable</p><p className="mt-1 text-sm text-red-200/70">{error}</p><button onClick={() => load()} className="mt-3 text-sm font-medium text-red-100 underline">Try again</button></div></div>
          </div>
        ) : loading ? (
          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/50 p-10 text-center text-zinc-500" role="status">Loading your work…</div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.72fr)]">
            <section className="space-y-3" aria-live="polite">
              {!content?.length ? (
                <EmptyState
                  title={activeTab === 'approvals' ? 'No approvals waiting' : activeTab === 'operations' ? 'No active provider work' : 'No requests yet'}
                  detail={activeTab === 'requests' ? 'Power and deletion requests you submit will appear here with their approval and provider status.' : 'This view updates automatically when new work needs attention.'}
                  action={activeTab === 'requests' ? (
                    <Link to="/resources" className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">
                      Browse resources
                    </Link>
                  ) : null}
                />
              ) : activeTab === 'operations' ? content.map((item) => <OperationCard key={item.id} operation={item} onOpen={openRequest} />) : content.map((item) => <WorkCard key={item.id} item={item} task={activeTab === 'approvals'} onOpen={openRequest} />)}
              {activeTab !== 'approvals' && (activeTab === 'requests' ? data.page?.next_request_cursor : data.page?.next_operation_cursor) && (
                <button onClick={loadMore} disabled={loadingMore} className="w-full rounded-lg border border-zinc-800 px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-900 disabled:opacity-50">{loadingMore ? 'Loading…' : 'Load more'}</button>
              )}
            </section>

            <aside className="min-h-[280px] rounded-xl border border-zinc-800 bg-zinc-900/60 p-5 lg:sticky lg:top-24 lg:self-start">
              {!selected ? (
                <div className="flex min-h-[240px] flex-col items-center justify-center text-center"><LuClock3 className="h-7 w-7 text-zinc-700" /><p className="mt-3 text-sm text-zinc-500">Choose a request to see its approval and execution history.</p></div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div><p className="text-xs uppercase tracking-wider text-zinc-500">Request detail</p><h2 className="mt-1 text-lg font-semibold">{selected.action ? `${statusLabel(selected.action)} ${selected.target?.display_name}` : selected.target?.display_name}</h2><p className="mt-1 text-xs text-zinc-500">Reference {selected.correlation_id || selected.id}</p></div>
                    <button onClick={() => { timelineSequence.current += 1; setSelected(null); setTimeline(null); }} className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300" aria-label="Close request detail"><LuX className="h-4 w-4" /></button>
                  </div>
                  {selected.reason && <div className="mt-4 rounded-lg bg-zinc-950/70 p-3 text-sm leading-6 text-zinc-300">{selected.reason}</div>}
                  <div className="mt-6"><Timeline timeline={timeline} loading={timelineLoading} /></div>
                  {(selected.viewer_actions?.can_decide || selected.viewer_actions?.can_cancel) && (
                    <div className="mt-6 border-t border-zinc-800 pt-4">
                      {selected.viewer_actions.can_decide && (
                        <>
                          <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={3} maxLength={500} placeholder="Optional approval note; required when rejecting" aria-label="Decision comment" className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-amber-500/60" />
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <button onClick={() => setDecision('reject')} disabled={busy} className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-300 disabled:opacity-50">Reject</button>
                            <button onClick={() => setDecision('approve')} disabled={busy} className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-300 disabled:opacity-50">Approve</button>
                          </div>
                          {decision && (
                            <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-100" role="alert">
                              <p>Record this {decision} decision? The request will use the latest verified approval policy.</p>
                              <div className="mt-3 flex gap-2"><button onClick={() => setDecision(null)} className="rounded-md px-3 py-1.5 text-zinc-300">Keep reviewing</button><button onClick={submitDecision} disabled={busy} className="rounded-md bg-amber-400 px-3 py-1.5 font-medium text-zinc-950 disabled:opacity-50">{busy ? 'Recording…' : 'Confirm'}</button></div>
                            </div>
                          )}
                        </>
                      )}
                      {selected.viewer_actions.can_cancel && <button onClick={cancelRequest} disabled={busy} className="mt-3 w-full rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">Cancel request</button>}
                    </div>
                  )}
                </>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyWork;
